import { useState } from 'react';
import {
  Card, CardContent, TextField, Button, Grid, Typography, Box,
  MenuItem, Snackbar, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, useMediaQuery,
  useTheme, Container, Divider, Chip, alpha, RadioGroup,
  FormControlLabel, Radio, FormControl, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Send, AccountBalance, TrendingUp, LocalAtm, Receipt, ViewList, AddCircle
} from '@mui/icons-material';
import * as colors from '../config/colorProfile';

const CashManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [view, setView] = useState('add'); // 'add' or 'list'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  // Cash in hand (from cashbook)
  const [cashInHand, setCashInHand] = useState(15450);

  const [transaction, setTransaction] = useState({
    type: 'deposit', // deposit or branch_transfer
    amount: '',
    destination: '', // bank or branch
    bankName: '',
    branchName: '',
    accountNumber: '',
    slipNumber: '',
    transactionDate: new Date().toISOString().split('T')[0],
    remarks: ''
  });

  const [denominations, setDenominations] = useState({
    2000: 0,
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
    5: 0,
    2: 0,
    1: 0,
    0.5: 0
  });

  // Demo transaction history
  const [transactionHistory, setTransactionHistory] = useState([
    {
      id: 'TXN001',
      date: '2025-11-28',
      type: 'deposit',
      destination: 'HDFC Bank',
      amount: 25000,
      slipNumber: 'SL123456',
      status: 'completed'
    },
    {
      id: 'TXN002',
      date: '2025-11-27',
      type: 'branch_transfer',
      destination: 'Main Branch',
      amount: 18500,
      slipNumber: 'BR001',
      status: 'completed'
    },
    {
      id: 'TXN003',
      date: '2025-11-26',
      type: 'deposit',
      destination: 'SBI',
      amount: 32000,
      slipNumber: 'SL123457',
      status: 'completed'
    },
    {
      id: 'TXN004',
      date: '2025-11-25',
      type: 'branch_transfer',
      destination: 'Regional Office',
      amount: 15000,
      slipNumber: 'BR002',
      status: 'completed'
    },
    {
      id: 'TXN005',
      date: '2025-11-24',
      type: 'deposit',
      destination: 'ICICI Bank',
      amount: 28750,
      slipNumber: 'SL123458',
      status: 'completed'
    }
  ]);

  const handleDenomChange = (denom, value) => {
    const numValue = parseInt(value) || 0;
    setDenominations(prev => ({
      ...prev,
      [denom]: numValue
    }));
  };

  const calculateDenomTotal = (denom) => {
    return parseFloat(denom) * (denominations[denom] || 0);
  };

  const getTotalAmount = () => {
    return Object.keys(denominations).reduce((total, denom) => {
      return total + calculateDenomTotal(denom);
    }, 0);
  };

  const totalAmount = getTotalAmount();
  const remainingCash = cashInHand - totalAmount;

  const validateForm = () => {
    if (totalAmount <= 0) {
      setSnackbar({ open: true, message: 'Please enter denomination details', severity: 'error' });
      return false;
    }

    if (totalAmount > cashInHand) {
      setSnackbar({ open: true, message: 'Amount exceeds cash in hand', severity: 'error' });
      return false;
    }

    if (!transaction.transactionDate) {
      setSnackbar({ open: true, message: 'Please select transaction date', severity: 'error' });
      return false;
    }

    if (transaction.type === 'deposit') {
      if (!transaction.destination) {
        setSnackbar({ open: true, message: 'Please select bank', severity: 'error' });
        return false;
      }
      if (!transaction.slipNumber) {
        setSnackbar({ open: true, message: 'Please enter deposit slip number', severity: 'error' });
        return false;
      }
    } else {
      if (!transaction.branchName) {
        setSnackbar({ open: true, message: 'Please enter branch name', severity: 'error' });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1500));

      const newTransaction = {
        id: `TXN${Date.now()}`,
        date: transaction.transactionDate,
        type: transaction.type,
        destination: transaction.type === 'deposit' ? transaction.destination.toUpperCase() : transaction.branchName,
        amount: totalAmount,
        slipNumber: transaction.slipNumber,
        status: 'completed'
      };

      setTransactionHistory([newTransaction, ...transactionHistory]);

      setSnackbar({ 
        open: true, 
        message: `Cash ${transaction.type === 'deposit' ? 'deposited' : 'transferred'} successfully! Reference: ${newTransaction.id}`, 
        severity: 'success' 
      });

      // Reset form
      setTimeout(() => {
        setTransaction({
          type: 'deposit',
          amount: '',
          destination: '',
          bankName: '',
          branchName: '',
          accountNumber: '',
          slipNumber: '',
          transactionDate: new Date().toISOString().split('T')[0],
          remarks: ''
        });
        setDenominations({
          2000: 0, 500: 0, 200: 0, 100: 0, 50: 0,
          20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0
        });
        // Update cash in hand
        setCashInHand(remainingCash);
        setView('list');
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return colors.SUCCESS_COLOR;
      case 'pending': return colors.ACTION_YELLOW_TEXT;
      case 'failed': return colors.ERROR_COLOR;
      default: return colors.TEXT_SECONDARY;
    }
  };

  const getTypeIcon = (type) => {
    return type === 'deposit' ? <AccountBalance sx={{ fontSize: 18 }} /> : <Send sx={{ fontSize: 18 }} />;
  };

  return (
    <Box sx={{ bgcolor: colors.PAGE_BG, minHeight: '100vh', py: isMobile ? 1 : 3, px: isMobile ? 0 : 2 }}>
      <Container maxWidth="lg" disableGutters={isMobile} sx={{ px: isMobile ? 0 : 2 }}>
        {/* Header */}
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY, mb: 0.5 }}>
              Cash Management
            </Typography>
            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
              Deposit cash to bank or transfer to branch
            </Typography>
          </Box>

          {/* View Toggle */}
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => newView && setView(newView)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderColor: colors.BORDER_COLOR,
                color: colors.TEXT_SECONDARY,
                '&.Mui-selected': {
                  bgcolor: colors.PRIMARY_COLOR,
                  color: 'white',
                  '&:hover': {
                    bgcolor: colors.PRIMARY_COLOR
                  }
                },
                '&:hover': {
                  bgcolor: colors.TOPBAR_HOVER
                }
              }
            }}
          >
            <ToggleButton value="add">
              <AddCircle sx={{ fontSize: 18, mr: 0.5 }} />
              {!isMobile && 'Add Transaction'}
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList sx={{ fontSize: 18, mr: 0.5 }} />
              {!isMobile && 'Transaction History'}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Cash Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3, px: isMobile ? 2 : 0 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.BORDER_COLOR}`, borderRadius: 2, bgcolor: colors.ACTION_YELLOW_BG }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <LocalAtm sx={{ fontSize: 36, color: colors.ACTION_YELLOW_TEXT, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 600, display: 'block' }}>
                  Cash in Hand
                </Typography>
                <Typography variant="h5" sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{cashInHand.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.BORDER_COLOR}`, borderRadius: 2, bgcolor: colors.ACTION_BLUE_BG }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <TrendingUp sx={{ fontSize: 36, color: colors.ACTION_BLUE_TEXT, mb: 1 }} />
                <Typography variant="caption" sx={{ color: colors.ACTION_BLUE_TEXT, fontWeight: 600, display: 'block' }}>
                  Transaction Amount
                </Typography>
                <Typography variant="h5" sx={{ color: colors.ACTION_BLUE_TEXT, fontWeight: 700, mt: 0.5 }}>
                  ₹{totalAmount.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ border: `1px solid ${colors.BORDER_COLOR}`, borderRadius: 2, bgcolor: colors.SUCCESS_COLOR }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <AccountBalance sx={{ fontSize: 36, color: 'white', mb: 1 }} />
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, display: 'block' }}>
                  Remaining Cash
                </Typography>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mt: 0.5 }}>
                  ₹{remainingCash.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Add Transaction View */}
        {view === 'add' && (
          <Card elevation={0} sx={{ 
            border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`, 
            borderRadius: isMobile ? 0 : 2,
            borderTop: isMobile ? `1px solid ${colors.BORDER_COLOR}` : 'none',
            bgcolor: colors.CARD_BG 
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              {/* Transaction Type */}
              <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
                Transaction Type
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  row
                  value={transaction.type}
                  onChange={(e) => setTransaction({ ...transaction, type: e.target.value, destination: '', branchName: '' })}
                >
                  <FormControlLabel 
                    value="deposit" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccountBalance sx={{ fontSize: 18 }} />
                        <Typography variant="body2">Bank Deposit</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="branch_transfer" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Send sx={{ fontSize: 18 }} />
                        <Typography variant="body2">Branch Transfer</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Divider sx={{ my: 3, borderColor: colors.DIVIDER_COLOR }} />

              {/* Cash Denomination */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.PRIMARY_COLOR, mb: 2 }}>
                Cash Denomination
              </Typography>

              <TableContainer component={Paper} elevation={0} sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`, 
                borderRadius: 1.5,
                mb: 3,
                maxHeight: isMobile ? 350 : 'none',
                overflowY: isMobile ? 'auto' : 'visible'
              }}>
                <Table size="small" stickyHeader={isMobile}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Denomination</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Quantity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                        Notes
                      </TableCell>
                    </TableRow>
                    {[2000, 500, 200, 100, 50, 20, 10].map(denom => (
                      <TableRow key={denom} hover>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>₹{denom}</TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={denominations[denom] === 0 ? '' : denominations[denom]}
                            onChange={(e) => handleDenomChange(denom, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                            sx={{ width: isMobile ? 60 : 80, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px' } }}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Box sx={{ 
                            display: 'inline-block', 
                            px: 1.5, 
                            py: 0.5, 
                            bgcolor: alpha(colors.SUCCESS_COLOR, 0.1), 
                            borderRadius: 1, 
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            color: colors.SUCCESS_COLOR 
                          }}>
                            ₹{calculateDenomTotal(denom).toLocaleString()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                        Coins
                      </TableCell>
                    </TableRow>
                    {[5, 2, 1, 0.5].map(denom => (
                      <TableRow key={denom} hover>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                          ₹{denom === 0.5 ? '0.50' : denom}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={denominations[denom] === 0 ? '' : denominations[denom]}
                            onChange={(e) => handleDenomChange(denom, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                            sx={{ width: isMobile ? 60 : 80, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px' } }}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Box sx={{ 
                            display: 'inline-block', 
                            px: 1.5, 
                            py: 0.5, 
                            bgcolor: alpha(colors.SUCCESS_COLOR, 0.1), 
                            borderRadius: 1, 
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            color: colors.SUCCESS_COLOR 
                          }}>
                            ₹{calculateDenomTotal(denom).toLocaleString()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: colors.PRIMARY_COLOR }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem', py: 1.5 }}>
                        Total Amount
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem', py: 1.5 }}>
                        ₹{totalAmount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3, borderColor: colors.DIVIDER_COLOR }} />

              {/* Transaction Details */}
              <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
                Transaction Details
              </Typography>

              <Grid container spacing={2}>
                {transaction.type === 'deposit' ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Select Bank"
                        required
                        value={transaction.destination}
                        onChange={(e) => setTransaction({ ...transaction, destination: e.target.value })}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      >
                        <MenuItem value="hdfc">HDFC Bank</MenuItem>
                        <MenuItem value="sbi">State Bank of India</MenuItem>
                        <MenuItem value="icici">ICICI Bank</MenuItem>
                        <MenuItem value="axis">Axis Bank</MenuItem>
                        <MenuItem value="kotak">Kotak Mahindra Bank</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        value={transaction.accountNumber}
                        onChange={(e) => setTransaction({ ...transaction, accountNumber: e.target.value })}
                        placeholder="Enter account number"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Deposit Slip Number"
                        required
                        value={transaction.slipNumber}
                        onChange={(e) => setTransaction({ ...transaction, slipNumber: e.target.value })}
                        placeholder="Enter slip number"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Branch Name"
                        required
                        value={transaction.branchName}
                        onChange={(e) => setTransaction({ ...transaction, branchName: e.target.value })}
                        placeholder="Enter branch name"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Receipt Number"
                        value={transaction.slipNumber}
                        onChange={(e) => setTransaction({ ...transaction, slipNumber: e.target.value })}
                        placeholder="Enter receipt number"
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Transaction Date"
                    type="date"
                    required
                    value={transaction.transactionDate}
                    onChange={(e) => setTransaction({ ...transaction, transactionDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks (Optional)"
                    multiline
                    rows={2}
                    value={transaction.remarks}
                    onChange={(e) => setTransaction({ ...transaction, remarks: e.target.value })}
                    placeholder="Add any additional notes"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>

              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={transaction.type === 'deposit' ? <AccountBalance /> : <Send />}
                onClick={handleSubmit}
                disabled={loading || totalAmount <= 0}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: colors.PRIMARY_COLOR,
                  boxShadow: colors.SHADOW_MD,
                  '&:hover': { 
                    boxShadow: colors.SHADOW_LG,
                    bgcolor: colors.PRIMARY_COLOR
                  }
                }}
              >
                {loading ? 'Processing...' : transaction.type === 'deposit' ? 'Submit Deposit' : 'Submit Transfer'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Transaction History View */}
        {view === 'list' && (
          <Card elevation={0} sx={{ 
            border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`, 
            borderRadius: isMobile ? 0 : 2,
            borderTop: isMobile ? `1px solid ${colors.BORDER_COLOR}` : 'none',
            bgcolor: colors.CARD_BG 
          }}>
            <CardContent sx={{ p: isMobile ? 0 : 3 }}>
              <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY, px: isMobile ? 2 : 0, pt: isMobile ? 2 : 0 }}>
                Transaction History
              </Typography>

              <TableContainer component={Paper} elevation={0} sx={{ 
                border: `1px solid ${colors.BORDER_COLOR}`, 
                borderRadius: isMobile ? 0 : 1.5,
                maxHeight: isMobile ? 'calc(100vh - 480px)' : '600px',
                overflowY: 'auto'
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                        Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                        Transaction ID
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                          Type
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                        Destination
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                        Amount
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1.5, bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                          Status
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactionHistory.map((txn) => (
                      <TableRow key={txn.id} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                        <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          {new Date(txn.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.TEXT_PRIMARY }}>
                            {txn.id}
                          </Typography>
                          {isMobile && (
                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              {getTypeIcon(txn.type)}
                              {txn.type === 'deposit' ? 'Deposit' : 'Transfer'}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                            <Chip
                              icon={getTypeIcon(txn.type)}
                              label={txn.type === 'deposit' ? 'Bank Deposit' : 'Branch Transfer'}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: txn.type === 'deposit' ? alpha(colors.ACTION_BLUE_TEXT, 0.1) : alpha(colors.ACTION_YELLOW_TEXT, 0.1),
                                color: txn.type === 'deposit' ? colors.ACTION_BLUE_TEXT : colors.ACTION_YELLOW_TEXT
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {txn.destination}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                            {txn.slipNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.ERROR_COLOR }}>
                            -₹{txn.amount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1.5 }}>
                            <Chip
                              label={txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: alpha(getStatusColor(txn.status), 0.1),
                                color: getStatusColor(txn.status)
                              }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {transactionHistory.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Receipt sx={{ fontSize: 64, color: colors.TEXT_SECONDARY, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body2" sx={{ color: colors.TEXT_SECONDARY }}>
                    No transactions found
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            sx={{ borderRadius: 1.5, width: '100%', boxShadow: colors.SHADOW_MD }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CashManagement;
